import React, { useEffect, useRef, useState } from 'react'
import { EditorState, RichUtils, SelectionState } from 'draft-js'
import Button from 'components/button'
import Menu from 'components/menu'
import Textarea from 'components/textarea'
import Tooltip from 'components/tooltip'
import { pressable } from 'components/editor/helpers/pressable'
import { BLUE_BUTTON } from 'components/save-buttons'
import { Link, Unlink } from 'components/icons'
import { IContextMenuState } from 'types'
import { INITIAL_CONTEXT_MENU_STATE, OVER_PANEL_Z_INDEX } from 'app-constants'

// Five lines of the link field, then it scrolls.
const LINK_FIELD_MAX_HEIGHT = 5 * 20 + 14

// A link as typed, made one that goes where it says: "morta.io" goes to https://morta.io, not to a page of Morta's.
// A path in the app ("/project/…") or on the page ("#section") stays as it is, and web, email and phone links keep
// their scheme. Any other scheme ("javascript:", "data:") is made a web address, so a link can never run script.
const SAFE_SCHEMES = /^(https?|mailto|tel):/i
export const linkHref = (url: string): string => {
  const trimmed = url.trim()
  if (SAFE_SCHEMES.test(trimmed) || trimmed.startsWith('/') || trimmed.startsWith('#')) return trimmed
  return `https://${trimmed}`
}

/**
 * General Anchor props
 */
interface GeneralAnchorControlProps {
  editorState: EditorState
  onChange: (nextEditorState: EditorState) => void
}

/**
 * Add anchor section
 */
interface AddAnchorFormProps extends GeneralAnchorControlProps {
  onClose: () => void
}

const AddAnchorForm: React.FC<AddAnchorFormProps> = ({ editorState, onChange, onClose }: AddAnchorFormProps) => {
  const [url, setUrl] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)
  // Opened to type a link in, so the field takes the caret.
  useEffect(() => {
    inputRef.current?.focus()
  }, [])
  // A long link wraps onto more lines as it's typed or pasted, up to a few, so all of it can be read and the caret
  // moved about in it.
  useEffect(() => {
    const field = inputRef.current
    if (!field) return
    field.style.height = 'auto'
    field.style.height = `${Math.min(field.scrollHeight + 2, LINK_FIELD_MAX_HEIGHT)}px`
  }, [url])

  const handleSubmit = () => {
    if (!url.trim()) return
    const content = editorState.getCurrentContent()
    // create new entity
    const contentStateWithEntity = content.createEntity('LINK', 'MUTABLE', { url: linkHref(url) })
    // get last entity key
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey()
    // update the content block of current editor state
    const newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity })

    onChange(RichUtils.toggleLink(newEditorState, newEditorState.getSelection(), entityKey))
    handleClose()
  }

  const handleClose = () => {
    setUrl('')
    onClose()
  }

  const handleKeyPressed = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleSubmit()
    } else if (event.key === 'Escape') {
      handleClose()
    }
  }

  // In the manner of shadcn/ui's popover with a field: the link typed in, and the button that adds it beside it.
  return (
    <div className="flex items-start gap-2 p-2" onKeyDown={(event) => handleKeyPressed(event)}>
      <Textarea
        ref={inputRef}
        id="link-target"
        aria-label="Link"
        rows={1}
        className="w-auto min-w-0 flex-1 break-all"
        placeholder="https://example.com"
        value={url}
        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setUrl(event.target.value.replace(/\n/g, ''))}
        onKeyDown={(event: React.KeyboardEvent<HTMLTextAreaElement>) => {
          // A link has no lines of its own: Enter adds it, as in a one-line field.
          if (event.key === 'Enter') event.preventDefault()
        }}
      />
      <Button
        internalType="outline"
        className={`h-8 shrink-0 ${BLUE_BUTTON}`}
        disabled={!url.trim()}
        onClick={handleSubmit}
      >
        Add link
      </Button>
    </div>
  )
}

interface AddAnchorIconProps {
  editorState: EditorState
  onClick: (event: React.MouseEvent<HTMLDivElement>) => void
  // Whether its popover is open: said with aria-expanded, so a second press closes it (see Menu).
  expanded: boolean
}

const AddAnchorIcon: React.FC<AddAnchorIconProps> = ({ editorState, onClick, expanded }: AddAnchorIconProps) => {
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const selection = editorState.getSelection()
    if (!selection.isCollapsed()) {
      onClick(event)
    }
  }

  return (
    <Tooltip content="Add link">
      <div
        aria-label="Add link"
        {...pressable()}
        aria-haspopup="dialog"
        aria-expanded={expanded}
        className="flex items-center hover:bg-surface-sunken transition-all rounded cursor-pointer p-[7px] mr-2.5 text-green"
        onClick={(event: React.MouseEvent<HTMLDivElement>) => handleClick(event)}
      >
        <Link />
      </div>
    </Tooltip>
  )
}

type AddAnchorControlProps = GeneralAnchorControlProps
export const AddAnchorControl: React.FC<AddAnchorControlProps> = ({ editorState, onChange }: AddAnchorControlProps) => {
  const [open, setOpen] = useState(false)
  const [anchorMenu, setAnchorMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)

  const anchorMenuWidth = 320

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const selection = editorState.getSelection()
    if (!selection.isCollapsed()) {
      setOpen(!open)
      setAnchorMenu({
        open: true,
        top: `${event.currentTarget.getBoundingClientRect().bottom + 10}px`,
        left: `${event.currentTarget.getBoundingClientRect().left}px`,
        right: 'auto',
        bottom: 'auto'
      })
    }
  }

  const handleClose = () => {
    setAnchorMenu(INITIAL_CONTEXT_MENU_STATE)
    setOpen(false)
  }

  return (
    <React.Fragment>
      <AddAnchorIcon editorState={editorState} onClick={(event) => handleClick(event)} expanded={anchorMenu.open} />
      {/* Nested: a press in it is a press in a menu it was opened from (Edit column's), which then stays open. */}
      <Menu
        id="anchor-menu"
        nested
        menuState={anchorMenu}
        setMenuState={setAnchorMenu}
        width={anchorMenuWidth}
        zIndex={OVER_PANEL_Z_INDEX}
      >
        <AddAnchorForm editorState={editorState} onChange={onChange} onClose={handleClose} />
      </Menu>
    </React.Fragment>
  )
}

/**
 * Remove anchor icon section
 */
type RemoveAnchorControlProps = GeneralAnchorControlProps
export const RemoveAnchorControl: React.FC<RemoveAnchorControlProps> = ({
  editorState,
  onChange
}: RemoveAnchorControlProps) => {
  const hadleRemoveAnchor = () => {
    const selection = editorState.getSelection()
    if (!selection.isCollapsed()) {
      onChange(RichUtils.toggleLink(editorState, editorState.getSelection(), null))
    }
  }
  return (
    <Tooltip content="Remove link">
      <div
        aria-label="Remove link"
        {...pressable()}
        className="flex items-center hover:bg-surface-sunken transition-all rounded cursor-pointer p-[7px] mr-2.5 text-red"
        onClick={() => hadleRemoveAnchor()}
      >
        <Unlink />
      </div>
    </Tooltip>
  )
}

/**
 * The link the selection starts in, if it starts in one: its address, and the whole stretch of text it covers (so
 * removing it takes all of it, not only the part selected).
 */
export const selectedLink = (editorState: EditorState): { url: string; selection: SelectionState } | null => {
  const selection = editorState.getSelection()
  const content = editorState.getCurrentContent()
  const block = content.getBlockForKey(selection.getStartKey())
  if (!block) return null
  const offset = selection.getStartOffset()
  const entityKey = block.getEntityAt(offset)
  if (!entityKey || content.getEntity(entityKey).getType() !== 'LINK') return null
  let range: SelectionState | null = null
  block.findEntityRanges(
    (character) => character.getEntity() === entityKey,
    (start, end) => {
      if (start <= offset && offset < end)
        range = SelectionState.createEmpty(block.getKey()).merge({
          anchorOffset: start,
          focusOffset: end
        }) as SelectionState
    }
  )
  return range ? { url: content.getEntity(entityKey).getData().url, selection: range } : null
}

/**
 * Over a link, in the formatting bubble: where it goes (cut in the middle when long, in full on hover, opening it),
 * and an unlink button that takes the link off the text, set apart from the formatting by a hairline.
 */
// How much of a long link's end stays when it's cut in the middle.
const LINK_TAIL = 10

export const LinkAtSelection: React.FC<GeneralAnchorControlProps> = ({ editorState, onChange }) => {
  const link = selectedLink(editorState)
  if (!link) return null
  const removeLink = () =>
    onChange(EditorState.forceSelection(RichUtils.toggleLink(editorState, link.selection, null), link.selection))
  const shown = link.url.replace(/^https?:\/\//, '')
  // One line, after the formatting and a hairline. A long link is cut in the middle, keeping where it starts and
  // how it ends: the start gives way with an ellipsis and the last few characters stay.
  const tail = shown.length > LINK_TAIL * 2 ? shown.slice(-LINK_TAIL) : ''
  const head = tail ? shown.slice(0, -LINK_TAIL) : shown
  return (
    <div className="flex min-w-0 !shrink items-center">
      <span aria-hidden className="mx-1 h-4 w-px shrink-0 bg-divider" />
      <Tooltip content={shown}>
        <a
          href={linkHref(link.url)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex min-w-0 flex-1 px-1 text-[13px] leading-6 whitespace-nowrap text-blue hover:underline"
        >
          <span className="min-w-0 truncate">{head}</span>
          {tail && <span className="shrink-0">{tail}</span>}
        </a>
      </Tooltip>
      <Tooltip content="Remove link">
        <Button
          internalType="transparent"
          aria-label="Remove link"
          className="size-6 shrink-0 rounded-md p-0 text-muted hover:bg-surface-hover hover:text-body"
          onClick={removeLink}
        >
          <Unlink />
        </Button>
      </Tooltip>
    </div>
  )
}
